import {
  Badge,
  Button,
  StatusBadge,
  ViewPlaceholder,
} from '@vrplatform/components';
import {
  showApiErrorNotification,
  useApiMutation,
  useApiQuery,
  useInvalidateApiQueries,
  useTeam,
} from '@vrplatform/data';
import {
  CalendarEventIcon,
  CircleDollarIcon,
  CreditCardIncomeIcon,
  HashtagIcon,
  Icon,
  LoaderIcon,
  NoteTextIcon,
  OfficeIcon,
} from '@vrplatform/icons';
import type { MRT_ColumnDef } from '@vrplatform/table';
import { Drawer } from '@vrplatform/components-legacy';
import type { ThemeColor } from '@vrplatform/theme';
import {
  type Maybe,
  day,
  formatCurrency,
  sum,
  toTitleCase,
} from '@vrplatform/utils';
import {
  Box,
  Center,
  Group,
  LoadingOverlay,
  Stack,
  Title,
  Tooltip,
  rem,
} from '@mantine/core';
import { Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import {
  DrawerCollapsableTable,
  DrawerHeader,
  DrawerInfoCard,
} from '../_components';
import { useDepositDetailDrawer } from '../deposit-drawer';
import { useExpenseDetailDrawer } from '../expense-drawer';
import { BankRecordEllipsisMenuItems } from './BankRecordEllipsisMenuItems';
import { BankRecordEllipsisMenuModals } from './BankRecordEllipsisMenuModals';
import { useBankRecordDetailDrawer } from './useBankRecordDetailDrawer';
import { useBankTransferDetailDrawer } from '../bank-transfer-drawer';

export const STORYBOOK_BANK_RECORD_DETAIL_QUERY_KEY =
  'bank-record-detail-drawer';

function useBankRecordQuery(id: Maybe<string>) {
  const query = useApiQuery(
    async (api, signal, args, $db) => {
      const bankRecordId = args.id;

      if (!bankRecordId) return null;

      const [bankRecord, bankAccount] = await Promise.all([
        api.GET('/bank-records/{id}', {
          params: {
            path: {
              id: bankRecordId,
            },
          },
          signal,
        }),
        $db(
          (q) =>
            q
              .accounts({
                where: {
                  bankRecords: {
                    id: {
                      _eq: bankRecordId,
                    },
                  },
                },
                limit: 1,
                order_by: [
                  {
                    id: 'asc_nulls_last',
                  },
                ],
              })
              .map((account) => ({
                id: account.id,
                title: account.title,
                bankingType: account.bankingType,
                connectionId: account
                  .bankingConnections({
                    limit: 1,
                    order_by: [{ createdAt: 'asc_nulls_last' }],
                  })
                  .map((x) => x.connectionId)[0],
              }))[0],
          {
            fetchOptions: {
              signal,
            },
          }
        ),
      ]);

      if (bankRecord.error) {
        throw bankRecord.error;
      }

      return bankRecord.data ? { ...bankRecord.data, bankAccount } : null;
    },
    {
      skip: !id,
      queryGroup: 'bank-records',
      uniqueQueryKey: 'bank-record-detail-drawer',
      storybookQueryKey: STORYBOOK_BANK_RECORD_DETAIL_QUERY_KEY,
      variables: {
        id,
      },
      queryKeys: ['bankRecord-detail-drawer'],
      errorTitle: 'Failed to fetch bank record',
    }
  );

  const [debounced] = useDebouncedValue(query.data, 500);

  return { ...query, data: query.data || debounced };
}

type BankRecord = NonNullable<ReturnType<typeof useBankRecordQuery>['data']>;

type PaidStatus = NonNullable<
  NonNullable<BankRecord['reconciliation']>['status']
>;

const BAGDE_OPTIONS: Record<PaidStatus, { label: string; color: ThemeColor }> =
  {
    overpaid: {
      label: 'Overpaid',
      color: 'red',
    },
    paid: {
      label: 'Matched',
      color: 'green',
    },
    unpaid: {
      label: 'Unmatched',
      color: 'yellow',
    },
    underpaid: {
      label: 'Underpaid',
      color: 'yellow',
    },
  };

export const BankRecordStatusBadge = ({
  status,
  matchedByBankRule,
}: {
  status: Maybe<PaidStatus>;
  matchedByBankRule: Maybe<boolean>;
}) => {
  if (!status || !BAGDE_OPTIONS[status]) return null;

  const sharedStatus =
    status === 'paid'
      ? 'success'
      : status === 'underpaid'
        ? 'underpaid'
        : 'pending';

  return (
    <Group wrap="nowrap" gap="xs">
      <StatusBadge
        label={BAGDE_OPTIONS[status].label}
        size="md"
        status={sharedStatus}
      />

      {matchedByBankRule && (
        <Tooltip label="Matched by bank rule">
          <Icon
            icon="ArrowSwapIcon"
            size={13}
            color={(theme) => theme.colors[theme.primaryColor][6]}
          />
        </Tooltip>
      )}
    </Group>
  );
};

export const BankRecordDetailDrawer = () => {
  const { opened, close, bankRecordId } = useBankRecordDetailDrawer();
  const { isLoading, data: bankRecord } = useBankRecordQuery(bankRecordId);

  const [modal, setModal] = useState<'status' | 'unmatched' | null>(null);

  const closeModal = () => setModal(null);

  return (
    <>
      <BankRecordEllipsisMenuModals
        bankRecord={bankRecord}
        statusModal={{
          opened: modal === 'status',
          closeModal,
        }}
        unreconcileModal={{
          opened: modal === 'unmatched',
          closeModal,
        }}
      />
      <Drawer opened={opened} onClose={close} size={550}>
        <DrawerHeader
          closeDrawer={close}
          loading={isLoading}
          type="Bank record"
          title={
            bankRecord && (
              <Group wrap="nowrap" gap="xs" mt={rem(5)}>
                <Box>
                  <Title order={4} fw={500} component="p" m={0}>
                    {bankRecord.description}
                  </Title>
                </Box>
              </Group>
            )
          }
          menuItems={
            bankRecord && (
              <BankRecordEllipsisMenuItems
                bankRecord={bankRecord}
                openStatusModal={() => setModal('status')}
                openUnmatchModal={() => setModal('unmatched')}
              />
            )
          }
        />
        {!bankRecord && !isLoading ? (
          <ViewPlaceholder
            title="No bank record found"
            description="Has the bank record been deleted?"
            icon="CommentQuestionIcon"
            backgroundColor="transparent"
          />
        ) : (
          <Content bankRecord={bankRecord} isLoading={isLoading} />
        )}
      </Drawer>
    </>
  );
};

const Content = ({
  bankRecord,
  isLoading,
}: {
  bankRecord: Maybe<BankRecord>;
  isLoading: boolean;
}) => {
  const [{ currency }] = useTeam();

  if (!bankRecord) return null;

  return (
    <Stack
      gap={'md'}
      mb="md"
      sx={{
        position: 'relative',
      }}
    >
      <DrawerInfoCard
        rows={[
          {
            icon: CircleDollarIcon,
            title: 'Amount',
            text: formatCurrency(bankRecord.amount / 100, currency),
          },
          {
            icon: LoaderIcon,
            title: 'Status',
            text: (
              <BankRecordStatusBadge
                status={bankRecord.reconciliation?.status}
                matchedByBankRule={bankRecord.matchedByBankRule}
              />
            ),
          },
          {
            icon:
              bankRecord.bankAccount.bankingType === 'creditCard'
                ? CreditCardIncomeIcon
                : OfficeIcon,
            title: 'Bank account',
            text: bankRecord.bankAccount.title,
          },
          {
            icon: CalendarEventIcon,
            title: 'Date',
            text: day(bankRecord.date).format('MMM D, YYYY'),
          },
          {
            icon: HashtagIcon,
            title: 'Reference',
            text: bankRecord.uniqueRef,
          },
          {
            icon: NoteTextIcon,
            title: 'Description',
            text: bankRecord.description,
          },
        ]}
      />

      <Lines
        lines={bankRecord.reconciliation.transactions ?? []}
        bankRecordId={bankRecord.id}
      />

      <LoadingOverlay
        visible={isLoading}
        loaderProps={{
          size: 'sm',
        }}
      />
    </Stack>
  );
};

type ExpenseLine = NonNullable<
  BankRecord['reconciliation']['transactions']
>[number];

const Lines = ({
  lines,
  bankRecordId,
}: {
  lines: ExpenseLine[];
  bankRecordId: string;
}) => {
  const [{ currency }] = useTeam();

  const { open: openExpense } = useExpenseDetailDrawer();
  const { open: openDeposit } = useDepositDetailDrawer();
  const { open: openBankTransfer } = useBankTransferDetailDrawer();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const total = useMemo(
    () => formatCurrency(sum(lines, (x) => x.amount) / 100, currency),
    [currency]
  );

  const invalidate = useInvalidateApiQueries([
    'bank-records',
    'accounts',
    'expenses',
    'deposits',
  ]);

  const { isPending: loadingMutation, mutateAsync } = useApiMutation(
    'put',
    '/bank-records/{id}',
    {
      onError: (error: any) => {
        showApiErrorNotification({
          title: 'Failed to update bank records',
          defaultMessage:
            'Failed to update bank records. Please reach out to support if the issue persists.',
          error,
        });
      },
      onSuccess: () => {
        invalidate();
      },
    }
  );

  const columns = useMemo<MRT_ColumnDef<ExpenseLine>[]>(
    () => [
      {
        header: 'Description',
        accessorKey: 'description',
      },
      {
        header: 'Badge',
        size: 100,
        Cell: ({ row }) => {
          const line = row.original;

          return <Badge>{toTitleCase(line.type)}</Badge>;
        },
      },
      {
        header: 'Split',
        accessorKey: 'centTotal',
        mantineTableBodyCellProps: {
          align: 'right',
        },
        mantineTableFooterCellProps: {
          align: 'right',
          sx: (theme) => ({
            paddingRight: theme.spacing.xs,
            fontWeight: 'bold',
          }),
        },
        size: 100,
        Cell: ({ row }) => {
          return formatCurrency(row.original.amount / 100, currency);
        },
        Footer: !lines.length ? undefined : () => total,
      },
    ],
    [currency, lines.length, total]
  );

  return (
    <DrawerCollapsableTable
      title="Matched transactions"
      rightSection={
        lines.length && (
          <Tooltip label="Reset all matched transactions" withArrow>
            <Button
              variant="light"
              loading={loadingMutation}
              size="xs"
              sx={(theme) => ({
                paddingInline: theme.spacing.xs,
                paddingTop: 0,
              })}
              onClick={async () => {
                await mutateAsync({
                  params: {
                    path: {
                      id: bankRecordId,
                    },
                  },
                  body: {
                    reconciledTransactionIds: [],
                  },
                });
              }}
              rightIcon={'CrossCircleIcon'}
              rightIconSize={14}
            >
              Unmatch
            </Button>
          </Tooltip>
        )
      }
      onRowClick={{
        handler: (row) => {
          if (row.original.type === 'expense')
            openExpense(row.original.id, 'push');
          if (row.original.type === 'deposit')
            openDeposit(row.original.id, 'push');
          if (row.original.type === 'transfer')
            openBankTransfer(row.original.id, 'push');
        },
        disabled: (row) => !row.original.type,
      }}
      rowData={lines}
      columns={columns}
      emptyRowsFallback={() => (
        <Center>
          <Text size="sm" c="gray">
            No matched transactions
          </Text>
        </Center>
      )}
    />
  );
};
